import React from 'react';
// import { ImageUploader } from 'src/components/shared/image-uploader';
import cls from 'classnames';
import { Steps } from '@/components/shared/steps';
import { ImageUploader } from '@/components';
import { useModal, useProfile } from '@/hooks';
import { Transition } from '@headlessui/react';
import { Form, Input, ReformSubmitHandler, Textarea } from '@kodkafa/reform';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClanControllerUploadAvatarApiArg,
  useClanControllerCreateMutation,
  useClanControllerIsNameAvailableMutation,
  useClanControllerUploadAvatarMutation,
} from '@/store/services/MidleNestApi.service';

export default function CreateClanView() {
  const { t } = useTranslation();

  const { hideModal } = useModal();
  const { reloadProfile } = useProfile();
  const [step, setStep] = React.useState<number>(0);
  const [file, setFile] = React.useState<File | null>(null);
  const [createClan, { data: clanId }] = useClanControllerCreateMutation();
  const [isNameAvailable] = useClanControllerIsNameAvailableMutation();
  const [uploadAvatar] = useClanControllerUploadAvatarMutation();

  // TODO: sacma bir yontem oldu, button tiklama eventine bu kontrolu koy
  const onSubmitHandler: ReformSubmitHandler<{ name: string; message: string }> = async (
    data,
    setError,
  ) => {
    let errorStep = 0;
    setStep(3);

    try {
      if (!clanId) {
        // create clan without avatar
        try {
          await createClan(data).unwrap();
          reloadProfile();
        } catch {
          setError('name', { message: t('Unexpected error, please try again!') });
          throw new Error('unexpected error');
        }
      }
      // change clan avatar
      if (file) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          await uploadAvatar(formData as ClanControllerUploadAvatarApiArg).unwrap();
        } catch {
          errorStep = 2;
          setError('image', { message: t('Image upload error!') });
          throw new Error('image upload error');
        }
      }

      setStep(4);
    } catch (err) {
      setFile(null);
      setStep(errorStep);
    }
  };

  const onChangeHandler: ReformSubmitHandler<{ name: string; message: string }> = async (
    data,
    setError,
  ) => {
    if (step == 0) {
      try {
        await isNameAvailable(data).unwrap();
        setError('name', { message: '' });
      } catch {
        setError('name', { message: t('Clan name is not available.') });
        return;
      }
    }
  };

  return (
    <div className='flex flex-col gap-y-2 w-full md:w-96'>
      <Steps />
      <Form onSubmit={onSubmitHandler} onChange={onChangeHandler}>
        <div className='flex justify-between items-center w-full mb-5'>
          {Array.from({ length: 3 }).map((_, index) => (
            <>
              <div
                className={cls(
                  'w-6 h-6 rounded-full flex items-center justify-center',
                  step >= index ? 'bg-[#1C64F2]' : 'bg-[#C3DDFD]',
                )}
              >
                {step >= index ? (
                  <i className='icon-check text-white' />
                ) : (
                  <div className='w-3 h-3 rounded-full bg-[#1C64F2]'></div>
                )}
              </div>{' '}
              {index !== 2 && <div className='flex-1 h-[1px] bg-[#E5E7EB] mx-4' />}
            </>
          ))}
        </div>

        <Transition
          show={step === 0}
          as='div'
          className='flex flex-col gap-y-4'
          enter='transition-all duration-150'
          enterFrom='opacity-0 translate-x-96'
          enterTo='opacity-100 translate-x-0'
          leaveFrom='opacity-0 translate-x-0'
          leaveTo='opacity-100 -translate-x-96'
        >
          <h2 className='text-center text-xl leading-7 font-bold'>{t('Let’s gets started.')}</h2>
          <div>
            <Input
              label={t('Clan Name')}
              name='name'
              placeholder={t('Enter clan name')}
              className='rounded-lg border border-gray-300 px-4'
              required
              autoComplete='off'
            />
          </div>
          <div className='flex w-full justify-end gap-x-2'>
            <button
              type='button'
              onClick={() => setStep(1)}
              className='px-4 py-1 text-sm rounded-md bg-blue-400 text-white transition-colors hover:bg-blue-500'
            >
              {t('Next')}
            </button>
          </div>
        </Transition>

        <Transition
          show={step === 1}
          as='div'
          className='flex flex-col gap-y-4'
          enter='transition-all duration-150'
          enterFrom='opacity-0 translate-x-96'
          enterTo='opacity-100 translate-x-0'
          leaveFrom='opacity-0 translate-x-0'
          leaveTo='opacity-100 -translate-x-96'
        >
          <div>
            <Textarea
              label={t('Clan Description')}
              name='message'
              className='!h-32 rounded-lg border border-gray-300 px-4'
            />
          </div>
          <div className='flex w-full justify-between gap-x-2'>
            <button
              type='button'
              onClick={() => setStep(0)}
              className='px-4 py-1 text-sm rounded-md border border-gray-300 text-gray-700'
            >
              {t('Prev')}
            </button>
            <button
              type='button'
              onClick={() => setStep(2)}
              className='px-4 py-1 text-sm rounded-md bg-blue-400 text-white transition-colors hover:bg-blue-500'
            >
              {t('Next')}
            </button>
          </div>
        </Transition>

        <Transition
          show={step === 2}
          as='div'
          className='flex flex-col gap-y-4'
          enter='transition-all duration-150'
          enterFrom='opacity-0 translate-x-96'
          enterTo='opacity-100 translate-x-0'
          leaveFrom='opacity-0 translate-x-0'
          leaveTo='opacity-100 -translate-x-96'
        >
          <h2 className='text-center text-xl leading-7 font-bold'>
            {t('Upload a Profile Picture')}
          </h2>
          <div>
            <ImageUploader setFile={setFile} />
          </div>
          <div className='flex w-full justify-between'>
            <button
              type='button'
              onClick={() => setStep(1)}
              className='px-4 py-1 text-sm rounded-md border border-gray-300 text-gray-700'
            >
              {t('Prev')}
            </button>
            <div className='flex gap-x-2'>
              <button
                type='submit'
                className='px-4 py-1 text-sm rounded-md border border-gray-300 text-gray-700'
                disabled={!!file}
              >
                {t('Skip')}
              </button>
              <button
                type='submit'
                className='px-4 py-1 text-sm rounded-md bg-blue-400 text-white transition-colors hover:bg-blue-500'
                disabled={!file}
              >
                {t('Submit')}
              </button>
            </div>
          </div>
        </Transition>

        <Transition
          show={step === 3}
          as='div'
          className='flex flex-col gap-y-8 items-center pb-4'
          enter='transition-all duration-150'
          enterFrom='opacity-0 translate-x-96'
          enterTo='opacity-100 translate-x-0'
          leaveFrom='opacity-0 translate-x-0'
          leaveTo='opacity-100 -translate-x-96'
        >
          <h2 className='text-center text-xl leading-7 font-bold'>{t('Creating Clan')}</h2>
          <div className='w-12 h-12 bg-gray-300 rounded-full animate-ping' />
        </Transition>

        <Transition
          show={step === 4}
          as='div'
          className='flex flex-col gap-y-8 items-center pb-4'
          enter='transition-all duration-150'
          enterFrom='opacity-0 translate-x-96'
          enterTo='opacity-100 translate-x-0'
          leaveFrom='opacity-0 translate-x-0'
          leaveTo='opacity-100 -translate-x-96'
        >
          <div className='flex w-16 h-16 bg-blue-500 rounded-full justify-center items-center text-3xl text-white'>
            <i className='icon-check' />
          </div>
          <h2 className='text-center text-2xl leading-8 font-medium text-slate-500'>
            {t('Clan is Created!')}
          </h2>
          <p className='px-8 text-center text-sm leading-5 font-normal text-slate-600'>
            {t('Your clan has been successfully created. You can access it from My Clan.')}
          </p>

          {clanId?.id && (
            <Link
              className='px-8 py-2 rounded-md bg-blue-500 text-white text-sm'
              onClick={() => hideModal()}
              to={'/clans/' + clanId?.id}
            >
              {t('My Clan')}
            </Link>
          )}
        </Transition>
      </Form>
    </div>
  );
}
